import { SlidersHorizontal } from "lucide-react";
import { useState, type ReactNode } from "react";
import { StaffDialog } from "#order-dialog.tsx";
import { useLanguage } from "#i18n.tsx";

/** Compact order heading and applied-filter summary, with draft controls in a modal. */
export function OrderToolbar({
  title,
  summary,
  modified,
  open,
  apply,
  reset,
  resetApplied,
  heading,
  children,
}: {
  readonly title: string;
  readonly summary: string;
  readonly modified: boolean;
  readonly open: () => void;
  readonly apply: () => void;
  readonly reset: () => void;
  readonly resetApplied?: () => void;
  readonly heading?: ReactNode;
  readonly children: ReactNode;
}) {
  const { t } = useLanguage();
  const [showFilters, setShowFilters] = useState(false);
  return (
    <>
      <header className="order-toolbar">
        <div className="order-toolbar-heading">
          {heading ?? <h1>{title}</h1>}
          <p
            className={`filter-summary${modified ? " modified" : ""}`}
            role="status"
            aria-label={t("orders.filters")}
          >
            {summary}
          </p>
        </div>
        <div className="order-toolbar-actions">
          {modified && resetApplied && (
            <button
              className="secondary toolbar-reset"
              type="button"
              onClick={resetApplied}
            >
              {t("orders.reset")}
            </button>
          )}
          <button
            className={`secondary filter-toggle${modified ? " modified" : ""}`}
            type="button"
            aria-haspopup="dialog"
            aria-expanded={showFilters}
            aria-label={t("orders.filters")}
            title={t("orders.filters")}
            onClick={(event) => {
              // Safari does not focus tapped buttons; give the modal a return target.
              event.currentTarget.focus({ preventScroll: true });
              open();
              setShowFilters(true);
            }}
          >
            <SlidersHorizontal aria-hidden="true" />
            <span className="filter-toggle-label">{t("orders.filters")}</span>
          </button>
        </div>
      </header>
      {showFilters && (
        <StaffDialog
          title={t("orders.filters")}
          closeLabel={t("orders.closeFilters")}
          presentation="filters"
          close={() => setShowFilters(false)}
        >
          <form
            className="dialog-form"
            onSubmit={(event) => {
              event.preventDefault();
              apply();
              setShowFilters(false);
            }}
          >
            <div className="dialog-body">{children}</div>
            <footer className="dialog-footer">
              <button className="secondary" type="button" onClick={reset}>
                {t("orders.reset")}
              </button>
              <button className="primary" type="submit">
                {t("orders.applyFilters")}
              </button>
            </footer>
          </form>
        </StaffDialog>
      )}
    </>
  );
}
